import {
	dxtToLocalServerTime,
	getMomentTz,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Grid } from "@material-ui/core";
import { ColDef, themeMaterial } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash-es";
import { useEffect, useMemo, useRef, useState } from "react";
import { useGridStyles } from "../../styles/gridStyles";
import { LadleStatus } from "../models/LadleStatus";
import { StatusBarReference } from "./status-bar-ag-grid";
import { StatusIndicator } from "./status-indicator";

type Props = {
	ladleStatus: LadleStatus[];
};

export const TableLadleStatus = ({ ladleStatus }: Props) => {
	const gridRef = useRef<AgGridReact>(null);
	const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
	const classes = useGridStyles();
	const { state } = useUTLSettingsContext();

	const MAX_HEAT_COUNTS = get(state, "LADLE_STATUS_MAX_HEAT_COUNTS", 100);
	const MAX_CONTACT_TIME = get(state, "LADLE_STATUS_MAX_CONTACT_TIME", 10000);

	const getStatusData = (params: any) => {
		if (!params.data.LastTappedDateTime) return { lastTappedDate: null };

		const now = getMomentTz()();
		const lastTapped = getMomentTz()(params.data.LastTappedDateTime);
		const diffDays = now.diff(lastTapped, "days");

		return {
			lastTappedDate: params.data.LastTappedDateTime,
			diffDays: diffDays,
		};
	};
	const frameworkComponents = {
		statusBarReference: StatusBarReference,
	};
	const statusBar = {
		statusPanels: [
			{
				statusPanel: "statusBarReference",
				align: "left",
			},
		],
	};
	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
			suppressHeaderMenuButton: true,
		};
	}, []);

	useEffect(() => {
		setColumnDefs([
			{
				field: "status",
				headerName: "Status",
				minWidth: 70,
				maxWidth: 70,
				cellRenderer: StatusIndicator,
				valueGetter: getStatusData,
				cellStyle: {
					padding: "0px",
					textAlign: "center",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				},
			},
			{
				field: "LadleName",
				headerName: "Ladle",
				minWidth: 100,
				maxWidth: 100,
				flex: 2,
			},
			{
				field: "HeatCount",
				headerName: "Total Heats",
				cellClassRules: {
					"max-limit": (params) => {
						return Number(params.value) > MAX_HEAT_COUNTS;
					},
				},
				minWidth: 100,
				flex: 1,
			},
			{
				field: "TotalContactTime",
				headerName: "Total Contact",
				minWidth: 120,
				flex: 1,
				cellClassRules: {
					"max-limit": (params) => {
						return Number(params.value) > MAX_CONTACT_TIME;
					},
				},
			},
			{
				field: "FirstHeatName",
				headerName: "First Heat Number",
				minWidth: 140,
				flex: 1,
			},
			{
				field: "FirstHeatDateTime",
				headerName: "First Heat Date",
				minWidth: 140,
				flex: 3,
				valueFormatter: (params) =>
					dxtToLocalServerTime(params.value, "MM/dd/yy HH:mm:ss"),
			},
			{
				field: "LastTappedHeatName",
				headerName: "Last Tapped Heat Number",
				minWidth: 200,
				flex: 1,
			},
			{
				field: "LastTappedDateTime",
				headerName: "Last Tap Date",
				minWidth: 140,
				flex: 3,
				valueFormatter: (params) =>
					dxtToLocalServerTime(params.value, "MM/dd/yy HH:mm:ss"),
			},
		]);
	}, []);

	return (
		<Grid container style={{ width: "100%", display: "flex" }}>
			<Grid
				item
				md={12}
				xs={12}
				style={{
					height: "78vh",
					minHeight: "37vh",
					width: "100%",
					border: "1px solid #e0e0e0",
					margin: "20px 0 10px",
				}}
			>
				<AgGridReact
					ref={gridRef}
					rowData={ladleStatus}
					columnDefs={columnDefs}
					rowHeight={33}
					headerHeight={32}
					animateRows={true}
					suppressContextMenu={true}
					suppressRowHoverHighlight={true}
					statusBar={statusBar}
					components={frameworkComponents}
				/>
			</Grid>
		</Grid>
	);
};
